/************************************** THIRD-PARTY MODULES ***************************************/
import React from 'react';
import ReactDOM from 'react-dom';
import {observer, inject} from 'mobx-react';
import Transition from 'react-transition-group/Transition';
import {get, omit, switchExpr} from 'mad-utils/lib/react';
import FocusTrap from 'focus-trap-react';
import cn from 'classnames';

/**************************************** PROJECT MODULES *****************************************/
import {onEscapePress, XCloseButton, ConfigProps} from '@canimmunize/core-imports';

/**************************************** TYPE DEFINITIONS ****************************************/
import {ModalProps} from './ci-modal-props';

/********************************************* CONFIG *********************************************/
import {animations, styleVals, defaultCIModalPropVals} from './ci-modal-base-config';
const {fadeEnter, fadeLeave, zoomEnter, zoomLeave} = animations;
const {ciModalBoxShadow, xButtonTopRightPos} = styleVals;

/********************************************* CONFIG *********************************************/
const defaultXButtonID = `x-close-button-ci-modal`;

/***************************************** MAIN COMPONENT *****************************************/
/**
 * Primary application modal (see export at bottom for more info)
 */
@inject('config')
@observer
class BaseCIModal extends React.Component<ModalProps & ConfigProps> {
    static defaultProps = defaultCIModalPropVals;

    /********************* DOM NODES **********************/
    /**
     * DOM node where modal is mounted
     */
    dialogNode = null;
    /**
     * DOM node where main app is mounted
     */
    applicationNode = null;

    /**
     * Grab DOM node where main application is mounted [Memoized]
     */
    getApplicationNode = () => {
        this.applicationNode = document.getElementById(
            this.props.applicationNodeId.replace(/#+/g, ``)
        );

        // Add aria-hidden attribute *after* focus has moved to modal element
        // setTimeout(() => this.applicationNode.setAttribute(`aria-hidden`, `true`), 0);

        return this.applicationNode;
    };

    /********** LIFECYCLE **********/
    /**
     * 1) Run onEnter cb
     * 2) Wait for main app DOM node to be grabbed
     * 3) Activate exit on esc key (note that this occurs for *everything*
     *    it`s not blocked by being in a nested modal)
     */
    componentDidMount() {
        if (this.props.onEnter) this.props.onEnter();
        this.getApplicationNode();
        if (this.props.escapeExits) document.addEventListener(`keydown`, this.runExitOnEscDown);
    }

    /**
     * Before close: shut off main app hide; remove escape key listener
     */
    componentWillUnmount() {
        this.applicationNode.setAttribute(`aria-hidden`, `false`);
        document.removeEventListener(`keydown`, this.runExitOnEscDown);
    }

    /********** EXIT CONDITION HANDLING **********/
    /**
     * Watch for click outside modal, exit if this occurs
     */
    private checkUnderlayClick = event =>
        !(this.dialogNode && this.dialogNode.contains(event.target)) && this.exit();

    /**
     * Exit on escape key pressed
     */
    private runExitOnEscDown = event => onEscapePress(this.exit)(event);

    /**
     * Run to quit modal
     *
     * To monkey patch it, put a function in alternateCloseAction slot on this
     * function (exit)
     * Any function in alternateCloseAction will run instead of the original,
     * basic close action
     * Original close action forever accessible in origCloseAction; this runs
     * if nothing present in alternateCloseAction
     *
     * Note that this param gets returned as the 1st arg in the render prop (closeModal)
     */
    private exit = Object.assign(
        () => {
            if (this.exit.alternateCloseAction) {
                return this.exit.alternateCloseAction();
            } else {
                return this.exit.origCloseAction();
            }
        },
        {
            alternateCloseAction: null as EventHandler,
            origCloseAction: () => {
                return this.props.closeModal ? this.props.closeModal() : null;
            },
        }
    );

    /********** BUILD PROP OBJECTS **********/
    /**
     * Constructs a style object to add to props of constructed dialog element,
     * for cases where props.includeDefaultStyles is set to true
     * If props.dialogStyle is also included, it merges the included "data-"
     * and "aria-" elements from it into the constructed default
     */
    private buildDialogStyle = (animationStyle: object) => {
        // Default object
        const dialogStyle = {
            // ****** Positioning & layout ******
            display: `inline-flex`,
            flexDirection: `column`,
            justifyContent: `center`,
            verticalAlign: `middle`,
            // ****** Size ******
            // ...(this.props.width && {
            //     width: this.props.width,
            //     maxWidth: this.props.width === `auto` ? `unset` : this.props.width || null,
            // }),
            ...{width: this.props.width},
            // maxWidth: this.props.width || null,
            height: this.props.height || null,
            // ****** Border ******
            borderRadius: 3,
            boxShadow: ciModalBoxShadow,
            // ****** Misc styling / look & feel ******
            textAlign: `left`,
            cursor: `default`,
            // ****** Animation ******
            ...animationStyle,
        } as React.CSSProperties;

        // Add all props in dialogStyle to style, overriding any declared above
        if (this.props.dialogStyle) {
            for (const key of Object.keys(this.props.dialogStyle)) {
                dialogStyle[key] = this.props.dialogStyle[key];
            }
        }

        return dialogStyle;
    };

    /**
     * Build props to pass to the underlay element
     */
    private buildUnderlayProps = (wrapperAnimationStyles: object) => {
        const {props} = this;

        let style = {} as React.CSSProperties;
        if (props.includeDefaultStyles) {
            style = {
                position: `fixed`,
                top: 0,
                left: 0,
                width: `100%`,
                height: `100%`,
                zIndex: 1050,
                overflowX: `hidden`,
                overflowY: `auto`,
                WebkitOverflowScrolling: `touch`,
                textAlign: `center`,
                ...wrapperAnimationStyles,
            };

            if (props.underlayColor) style.background = props.underlayColor;
            if (props.clickOutsideExits) style.cursor = `pointer`;
        }

        if (props.underlayStyle) {
            for (const key in props.underlayStyle) {
                if (!props.underlayStyle.hasOwnProperty(key)) continue;
                style[key] = props.underlayStyle[key];
            }
        }

        type UnderlayProps = typeof props.underlayProps;
        const underlayProps = {
            className: props.underlayClassName || ``,
            style,
        } as UnderlayProps;

        // Set clicking underlay to close modal, if set to do so
        if (props.clickOutsideExits) underlayProps.onClick = this.checkUnderlayClick;

        // Apply custom underlayProps to style - including overwriting defaults
        for (const prop in this.props.underlayProps) {
            if (this.props.underlayProps.hasOwnProperty(prop)) {
                underlayProps[prop] = this.props.underlayProps[prop];
            }
        }
        return underlayProps;
    };

    /**
     * Build the X button on the top right corner
     */
    public xButton = () => (
        <XCloseButton
            onClick={get(this, `props.xButtonProps.onClick`, this.exit)}
            id={get(this, `props.xButtonProps.id`, defaultXButtonID)}
            {...this.props.xButtonProps && omit(this.props.xButtonProps, [`onClick`, `id`])}
        />
    );

    /**
     * Render portal & current state of modal within - with animation styles
     * applied
     */
    renderPortal = (wrapperAnimationStyles: object = {}, dialogAnimationStyles: object = {}) => {
        const xButton = this.xButton();

        const {initialFocus, clickOutsideExits, escapeExits, hideXButton} = this.props;

        const focusTrapOptions = {
            ...this.props.focusTrapOptions,
            ...{
                initialFocus:
                    initialFocus ||
                    (!hideXButton && defaultXButtonID.elID) ||
                    this.props.dialogId ||
                    defaultCIModalPropVals.dialogId,
            },
            ...(clickOutsideExits && {
                clickOutsideDeactivates: clickOutsideExits,
            }),
            escapeDeactivates: escapeExits,
        };

        /**
         * Standalone element to help vertically center the modal
         * Sibling of inner dialog box content component (DialogContentWrapper)
         */
        const VCenterHelper = () => (
            <div
                key={`cimodal-vcenter-helper`}
                style={{
                    ...(this.props.includeDefaultStyles && {
                        display: `inline-block`,
                        height: `100%`,
                        verticalAlign: `middle`,
                    }),
                }}
            />
        );

        VCenterHelper.displayName = `VCenterHelper`;

        const cleanID = (this.props.dialogId || defaultCIModalPropVals.dialogId).replace(/#/g, ``);

        return ReactDOM.createPortal(
            <FocusTrap paused={this.props.focusTrapPaused} focusTrapOptions={focusTrapOptions}>
                {/*
                 * Underlay filling screen outside modal, and wrapper for modal itself
                 * (modal is technically "contained" in the underlay)
                 */}
                <div {...this.buildUnderlayProps(wrapperAnimationStyles)}>
                    <VCenterHelper />
                    {/*
                     * Wrapper component for inner modal content (innermost dialog wrapper)
                     *
                     * Displays actual visible "card" in UI
                     *
                     * Directly contains X button (if present), and output of the render
                     * prop function
                    */}
                    <div
                        role={`dialog`}
                        id={cleanID}
                        key={`cimodal-inner-content-area-wrapper`}
                        aria-label={this.props.titleText}
                        className={cn(
                            `ci-modal-base-width`,
                            this.props.dialogClassName || `bg-white`
                        )}
                        style={
                            this.props.includeDefaultStyles
                                ? this.buildDialogStyle(dialogAnimationStyles)
                                : this.props.dialogStyle
                        }
                        ref={el => (this.dialogNode = el)}
                    >
                        {this.props.hideXButton ? null : xButton}
                        {this.props.render(this.exit, xButton)}
                    </div>
                </div>
            </FocusTrap>,
            // Mount point in DOM
            document.getElementById(this.props.mountNodeID || `app-fixed-modal-popover`)
        );
    };

    /**
     * Base modal rendering: determine which portal->modal to render based on
     * current state
     */
    render() {
        return (
            <Transition
                in={this.props.isOpen}
                timeout={this.props.config.animations.modalHideLengthMs}
                mountOnEnter={true}
                unmountOnExit={true}
            >
                {state =>
                    // prettier-ignore
                    switchExpr(
                        state === `entering`, this.renderPortal(fadeEnter, zoomEnter),
                        state === `exiting`,  this.renderPortal(fadeLeave, zoomLeave),
                        state === `entered` || state === `exited`, this.renderPortal(), null)
                }
            </Transition>
        );
    }
}

/********************************************* EXPORT *********************************************/
/**
 * Primary application modal: All modals in the app should be created with this
 * component
 *
 * Provides:
 *     - Accessibility: Auto-focus 1st element, return focus on close, focus
 *                      trap in modal
 *     - Animations on open & close
 *     - Sane default styles
 *     - An "X" button component returned in a render prop
 *     - A wrapped closeModal component for ensuring modal close
 *
 * TODO Determine if this is still needed or if we can direct export CIModal
 */
@observer
export class CIModal extends React.Component<ModalProps> {
    render() {
        return <BaseCIModal {...this.props} />;
    }
}
